"use client";

import React, { useState } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

// Answer formatting: **text** renders bold, lines starting with "• " render as
// a bullet list, and every other line is its own paragraph.
const cairaFAQs = [
  {
    question: "What is CAIRA?",
    answer:
      "CAIRA (Certified AI-Ready Accountant) is Miles Education’s AI credential built specifically for accountants. Across 3 levels and 90 hours, you learn to build AI agents and workflows for finance, audit and tax. You build them on the Microsoft tools firms already use: Copilot, Copilot Studio, Power Automate and Power BI. Each level earns 30 NASBA-approved CPE credits and a Credly badge you can showcase on LinkedIn.",
  },
  {
    question: "Is CAIRA free?",
    answer:
      "No, CAIRA is a paid credential. You can take it on its own for ₹89,000, or bundle it with U.S. CPA, CMA or EA and get CAIRA at a discounted ₹50,000. What is free is AI in Accounting 101, a live webinar every Saturday at 4 PM IST. In it, you build your first AI agent in 2 hours and earn a badge for LinkedIn.",
  },
  {
    question: "Do I need to know coding?",
    answer:
      "No. CAIRA is built for accountants, not programmers. You build agents and workflows with no-code Microsoft tools, and your knowledge of accounting, finance, audit and tax is the real input. If you understand how a reconciliation, an audit sample or a tax return works, you’re ready to start.",
  },
  {
    question:
      "Why does CAIRA use Microsoft Copilot rather than ChatGPT or Claude?",
    answer:
      "Public AI tools aren’t meant for client-confidential data, which is why many finance and accounting teams use Microsoft Copilot and Copilot Studio. These tools offer the same generative and agentic AI capabilities, but the data stays inside the company’s secure environment. They also work directly within Excel, Outlook, Teams, Word and PowerPoint. CAIRA trains you on the AI your employer is most likely to deploy.",
  },
  {
    question: "Who should take CAIRA?",
    answer:
      "CAIRA is for anyone in, or heading into, accounting, finance, audit or tax. That includes CA, CPA, CMA and EA aspirants, working professionals in CFO teams, and staff and leaders at CPA firms. If you want to be the person who brings AI into your team, CAIRA is built for you.",
  },
  {
    question: "Can college students enrol in CAIRA?",
    answer:
      "Yes, UG students can enrol. Starting during college means you graduate with practical AI skills, Credly badges on LinkedIn and a portfolio of agents you’ve built yourself. That helps you stand out when applying to Big 4 firms and MNCs.",
  },
  {
    question: "Can I take CAIRA without CPA, CMA or EA?",
    answer:
      "Yes. CAIRA is available as a standalone credential at ₹89,000. If you’re also planning a U.S. qualification, bundling brings CAIRA down to ₹50,000, and the two are designed to run side by side.",
  },
  {
    question: "Is CAIRA an alternative to CPA, CMA or EA?",
    answer:
      "No, it’s a complement. CPA, CMA or EA gives you the core credential in accounting, audit, tax or finance. CAIRA adds the AI skill layer on top: the ability to build the agents and workflows firms are now hiring for. Together, they make the strongest profile for the AI era, which is why CAIRA is integrated into Miles’ CPA, CMA and EA courses.",
  },
  {
    question: "What are the 3 levels of CAIRA?",
    answer:
      "**Level 1, AI Mindset, Skillset & Toolset:** build AI agents and workflows on the Microsoft stack (Copilot, Copilot Studio, Power Automate, Power BI).\n**Level 2, Applied AI Across Functions:** deploy agentic AI in audit, tax and CAS/CFO teams, including the build vs buy decision.\n**Level 3, Firm-Wide AI Operating Model:** lead AI transformation with governance, controls, agent evaluation and a deployment playbook.",
  },
  {
    question: "Which tools will I learn?",
    answer:
      "You’ll learn four tools:\n• **Microsoft Copilot** in Excel, Word, PowerPoint, Outlook and Teams, for everyday drafting, analysis and summaries.\n• **Copilot Studio**, for building AI agents grounded in your SOPs.\n• **Power Automate**, for workflows with approvals, reminders and a full audit trail.\n• **Power BI with Microsoft Fabric**, for live dashboards and exception tracking.",
  },
  {
    question: "What kind of AI agents will I build?",
    answer:
      "You’ll build agents that handle real accounting work:\n• **For CFO teams:** an AP 3-Way Match Agent, a Bank Reconciliation Agent and an AR Collection Agent.\n• **For audit:** a PBC Tracker Agent, a Lease Audit Agent and a Revenue Recognition Agent.\n• **For tax:** a 1099 Agent, a Tax Estimation Agent and an R&D Credits Agent.\nYou finish with a portfolio of agents you can show, not just a certificate.",
  },
  {
    question: "What is Miles AI Labs?",
    answer:
      "Miles AI Labs is the world’s first and only AI lab for accountants, and it’s included with CAIRA. It’s where you build your agents and workflows hands-on, instead of just watching demos. You don’t need Copilot Studio tokens, Power Automate licences or IT approvals. It’s a safe sandbox with no firm systems or client data, so there’s no confidentiality risk.",
  },
  {
    question: "What is Judgement Studio?",
    answer:
      "Judgement Studio is included with CAIRA. It puts you in realistic workplace scenarios, such as a client pressuring you to recognise revenue that doesn’t qualify, or a partner who wants to close a file despite a possible material discrepancy. You talk it through with an AI avatar who pushes back, and you’re scored on your professional judgement. It builds the scepticism, critical thinking and communication that matter more as routine work moves to AI.",
  },
  {
    question: "How is CAIRA delivered, and who teaches it?",
    answer:
      "CAIRA is online and self-paced. You learn through masterclasses, live webinars, podcasts, reels and AI Challenges on the Miles app. You can study on desktop or mobile, even offline, and pick up right where you left off. Masterclasses are led by global accounting innovators, including leaders of top CPA firms and finance teams. The live webinars are mapped to each CAIRA level.",
  },
  {
    question: "How long does CAIRA take to complete?",
    answer:
      "CAIRA is self-paced. On the recommended track of around 4 hours a week, you earn your Level 1 badge in about 4 months, Level 2 by month 8 and Level 3 by month 12. If you’re doing CPA, CMA or EA alongside, the CAIRA levels are timed to run in parallel with your exams.",
  },
  {
    question: "Will I earn CPE credits?",
    answer:
      "Yes. Each level carries 30 NASBA-approved CPE credits, which adds up to 90 across all 3 levels. Your credits are tracked in the Miles app, and your CPE certificate is available as soon as you earn it.",
  },
  {
    question: "What do I receive when I complete each level?",
    answer:
      "You receive a Credly digital badge for each level, which you can showcase on LinkedIn, along with that level’s 30 CPE credits. Complete all 3 levels to become a Certified AI-Ready Accountant.",
  },
  {
    question: "What is the fee for CAIRA?",
    answer:
      "CAIRA on its own costs ₹89,000. The fee is all-inclusive, covering Miles AI Labs and the certification fee. You can pay ₹30,000 upfront and the balance in 12 monthly EMIs of ₹4,917, with zero interest and zero processing fees.",
  },
  {
    question: "How much do I save by bundling CAIRA with CPA, CMA or EA?",
    answer:
      "When bundled, CAIRA costs ₹50,000 instead of ₹89,000, a saving of ₹39,000.\n• **CPA + CAIRA:** ₹1,49,000, or ₹30,000 upfront and ₹9,917 a month for 12 months\n• **CMA + CAIRA:** ₹1,49,000, or ₹30,000 upfront and ₹9,917 a month for 12 months\n• **EA + CAIRA:** ₹1,09,000, or ₹20,000 upfront and ₹7,417 a month for 12 months\nAll EMIs are zero interest, with zero processing fees.",
  },
  {
    question: "Are there any other costs?",
    answer:
      "Not for CAIRA itself, since the fee is all-inclusive. If you bundle it with a U.S. qualification, fees payable to the exam and licensing bodies are separate:\n• **CPA:** credential evaluation and exam fees.\n• **CMA:** IMA membership, certification and exam fees. As an IMA Global Diamond Partner, Miles gets CMA candidates a discount of ₹50,000+ on IMA fees when they’re paid along with the Miles enrolment.\n• **EA:** exam fees.",
  },
  {
    question: "Does CAIRA include placement support?",
    answer:
      "Yes. CAIRA comes with 360-degree placement support, including when you take it as a standalone credential. Miles works with Big 4 firms and 220+ MNCs that are hiring AI-ready accountants.",
  },
  {
    question: "How will CAIRA help my career?",
    answer:
      "Firms now ask a new hiring question: can you help us implement AI across finance, audit and tax? CAIRA lets you answer yes, with proof. You get Credly badges on LinkedIn, a portfolio of working agents to discuss in interviews, and the skills to move towards the advisory and higher-value work firms are reorganising around.",
  },
  {
    question: "What is the AI in Accounting 101 webinar?",
    answer:
      "AI in Accounting 101 is a free, 2-hour live webinar where you build your first AI agent. It runs every Saturday from 4 PM to 6 PM IST. It’s also the first webinar of CAIRA Level 1, so it’s the easiest way to experience CAIRA before you enrol. Attendees earn the AI in Accounting 101 badge to showcase on LinkedIn.",
  },
  {
    question: "How do I register for the webinar?",
    answer:
      "Fill in the form on this page with your name, phone number and email. We’ll send you the invite for the upcoming Saturday session.",
  },
];

const VISIBLE_COUNT = 5;

const formatBoldText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-[#E5E5E5]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const formatAnswer = (answer: string) => {
  // Group consecutive "• " lines into one list, everything else into paragraphs
  const blocks: { type: "p" | "ul"; lines: string[] }[] = [];
  answer.split("\n").forEach((line) => {
    const isBullet = line.startsWith("• ");
    const text = isBullet ? line.slice(2) : line;
    const last = blocks[blocks.length - 1];
    if (isBullet && last?.type === "ul") {
      last.lines.push(text);
    } else {
      blocks.push({ type: isBullet ? "ul" : "p", lines: [text] });
    }
  });

  return blocks.map((block, i) =>
    block.type === "ul" ? (
      <ul key={i} className="list-disc pl-5 space-y-1">
        {block.lines.map((line, j) => (
          <li key={j}>{formatBoldText(line)}</li>
        ))}
      </ul>
    ) : (
      <p key={i}>{formatBoldText(block.lines[0])}</p>
    ),
  );
};

const CairaFaqNew: React.FC = () => {
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="w-full bg-black">
      <div className="main_container mx-auto px-4 md:px-5 pt-6 pb-6 md:py-[50px] flex flex-col gap-6 md:gap-6">
        <h2 className="text-center text-[#FFBA08] text-[22px] md:text-[30px] font-bold leading-tight">
          Frequently Asked Question
        </h2>

        <AccordionPrimitive.Root
          type="single"
          collapsible
          defaultValue="item-0"
          className="w-full max-w-[700px] mx-auto flex flex-col gap-2.5 md:gap-1.5"
        >
          {/* Hidden items stay in the DOM so the full FAQ remains crawlable */}
          {cairaFAQs.map((faq, index) => (
            <AccordionPrimitive.Item
              key={index}
              value={`item-${index}`}
              className={`rounded-[16px] md:rounded-[16px] bg-[#141414] ${
                !showAll && index >= VISIBLE_COUNT ? "hidden" : ""
              }`}
            >
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger className="group flex flex-1 items-center justify-between gap-4 text-left px-4 md:px-[18px] py-[18px] md:py-3 text-white font-medium text-[14px] md:text-[16px] leading-snug">
                  <span>
                    {index + 1}. {faq.question}
                  </span>
                  <span
                    aria-hidden="true"
                    className="shrink-0 w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#8A8A8A] transition-transform duration-200 group-data-[state=open]:rotate-180 md:border-x-[5px] md:border-t-[5px]"
                  />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="px-4 md:px-[14px] pb-5 md:pb-3.5 text-[rgba(255,255,255,0.70)] text-[12px] md:text-[14px] leading-[1.75] md:leading-[1.7] space-y-2">
                  {formatAnswer(faq.answer)}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          ))}
        </AccordionPrimitive.Root>

        {cairaFAQs.length > VISIBLE_COUNT && (
          <button
            type="button"
            onClick={() => setShowAll((prev) => !prev)}
            className="mx-auto text-[#39f] text-sm underline underline-offset-2 hover:text-[#6AA8F0] font-medium"
          >
            {showAll ? "See Less" : "See More"}
          </button>
        )}
      </div>
    </div>
  );
};

export default CairaFaqNew;
